// @ts-nocheck
/*
 * Port of views/dashboard/dashboard-users-camp-count.js.
 * API: API_DASHBOARD_STATS (single GET). Logic byte-identical:
 *   - 1-minute refetch cooldown via lastFetchedAt timestamp
 *   - toast on error / cooldown
 *   - CALL_CENTRE_AGENT early return
 *   - Per-role card visibility (see DASHBOARD-INVENTORY.md §5.5 for the
 *     COMPANY_ADMIN / CALL_CENTRE_ADMIN / CALL_CENTRE_TEAM_LEAD exclusions —
 *     preserved verbatim)
 * Card count preserved: up to 11 tiles depending on role.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Building2,
  CalendarDays,
  Megaphone,
  Phone,
  RefreshCw,
  Shield,
  User,
  UserPlus,
  Users,
} from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { getApiCallShared } from '@/service/sharedGet';
import { API_DASHBOARD_STATS } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';
import { Card } from '@/components/ui/card';

const REFETCH_COOLDOWN_MS = 60 * 1000;

// Tone palette — mirrors KpiTile in KpiWidgets.tsx so the Platform Overview
// tiles read on the same colour language as the KPI strip.
//   info    – sky, workforce / real-time readings
//   positive – emerald, growth / activity
//   accent  – violet, platform / hierarchy inventory
//   warning – amber (unused here)
//   danger  – rose (unused here)
const TONE_CLASSES: Record<string, string> = {
  info: 'bg-sky-100 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400',
  positive:
    'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
  accent:
    'bg-violet-100 text-violet-600 dark:bg-violet-950/40 dark:text-violet-400',
  warning: 'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
  danger: 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400',
  neutral: 'bg-primary/10 text-primary',
};

function DashboardCard({ title, value, icon, highlight = false, tone = 'neutral' }) {
  const toneCls = TONE_CLASSES[tone] ?? TONE_CLASSES.neutral;
  return (
    <div className="flex items-center gap-3 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${toneCls}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-muted-foreground">{title}</p>
        <h2
          className={
            'text-lg font-bold ' +
            (highlight ? 'text-primary' : 'text-foreground')
          }
        >
          {value}
        </h2>
      </div>
    </div>
  );
}

function DashboardLiveStatusCard({ title, total, profileLive, dashboardLive, icon, tone = 'info' }) {
  const toneCls = TONE_CLASSES[tone] ?? TONE_CLASSES.info;
  return (
    <div className="flex items-start gap-3 shadow-neu-raised rounded-lg bg-card p-4 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${toneCls}`}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-muted-foreground">{title}</p>
        <h2 className="text-lg font-bold text-foreground">{total}</h2>
        <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Profile: <strong className="text-foreground">{profileLive}</strong>
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-500" />
            Dashboard: <strong className="text-foreground">{dashboardLive}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}

export default function DashboardUsersCampCount() {
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loggedInUserRole, setLoggedInUserRole] = useState(null);
  const [refetching, setRefetching] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(0);

  useEffect(() => {
    async function checkUserRole() {
      const rawUser = Storage.getItem('user');
      if (!rawUser) {
        console.error('User not found in Storage');
        return null;
      }
      let user;
      try {
        user = JSON.parse(rawUser);
      } catch (e) {
        console.error('Invalid JSON in user from Storage', e);
        return null;
      }
      if (user.role) setLoggedInUserRole(user.role);
      if (user.role === USER_ROLES.SUPERADMIN) setIsSuperAdmin(true);
    }
    checkUserRole();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const res = await getApiCallShared(API_DASHBOARD_STATS);
      setDashboardStats(res);
      setLastFetchedAt(Date.now());
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Error Fetching Dashboard Stats');
    } finally {
      setLoading(false);
    }
  };

  const handleRefetch = async () => {
    const elapsed = Date.now() - lastFetchedAt;
    if (elapsed < REFETCH_COOLDOWN_MS) {
      const wait = Math.ceil((REFETCH_COOLDOWN_MS - elapsed) / 1000);
      toast.info(`Please wait ${wait}s before refreshing again`);
      return;
    }
    try {
      setRefetching(true);
      const res = await getApiCall(API_DASHBOARD_STATS);
      setDashboardStats(res);
      setLastFetchedAt(Date.now());
    } catch (error) {
      toast.error(friendlyApiError(error) || 'Error Fetching Dashboard Stats');
    } finally {
      setRefetching(false);
    }
  };

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole == USER_ROLES.CALL_CENTRE_AGENT) return;
    fetchDashboardData();
  }, [loggedInUserRole]);

  if (loading) {
    return (
      <Card className="mb-6 p-6 text-center text-sm text-muted-foreground">
        Loading platform overview…
      </Card>
    );
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-bold text-foreground">Platform Overview</span>
        <button
          type="button"
          title="Refresh"
          onClick={refetching ? undefined : handleRefetch}
          disabled={refetching}
          className={
            'flex h-7 w-7 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors ' +
            (refetching
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-muted hover:text-foreground')
          }
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (refetching ? 'animate-spin' : '')} />
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {isSuperAdmin && (
          <DashboardCard
            title="Sub Super Admins"
            value={dashboardStats?.users?.subSuperAdmin || '0'}
            icon={<Shield className="h-4 w-4" />}
            tone="accent"
          />
        )}
        {loggedInUserRole !== USER_ROLES.COMPANY_ADMIN &&
          loggedInUserRole !== USER_ROLES.CALL_CENTRE_ADMIN &&
          loggedInUserRole !== USER_ROLES.CALL_CENTRE_AGENT &&
          loggedInUserRole !== USER_ROLES.CALL_CENTRE_TEAM_LEAD && (
            <DashboardCard
              title="Total Resellers"
              value={dashboardStats?.users?.reseller || '0'}
              icon={<Users className="h-4 w-4" />}
              tone="accent"
            />
          )}
        {loggedInUserRole !== USER_ROLES.CALL_CENTRE_ADMIN &&
          loggedInUserRole !== USER_ROLES.CALL_CENTRE_AGENT &&
          loggedInUserRole !== USER_ROLES.CALL_CENTRE_TEAM_LEAD && (
            <DashboardCard
              title="Total Companies"
              value={dashboardStats?.users?.companyAdmin || '0'}
              icon={<Building2 className="h-4 w-4" />}
              tone="accent"
            />
          )}
        {loggedInUserRole !== USER_ROLES.CALL_CENTRE_AGENT && (
          <DashboardLiveStatusCard
            title="Team Leads"
            total={dashboardStats?.liveStatus?.teamLeads?.total || 0}
            profileLive={dashboardStats?.liveStatus?.teamLeads?.liveProfile || 0}
            dashboardLive={dashboardStats?.liveStatus?.teamLeads?.liveDashboard || 0}
            icon={<User className="h-4 w-4" />}
            tone="info"
          />
        )}
        <DashboardLiveStatusCard
          title="Total Agents"
          total={dashboardStats?.liveStatus?.agents?.total || 0}
          profileLive={dashboardStats?.liveStatus?.agents?.liveProfile || 0}
          dashboardLive={dashboardStats?.liveStatus?.agents?.liveDashboard || 0}
          icon={<Phone className="h-4 w-4" />}
          tone="info"
        />
        <DashboardCard
          title="All Users"
          value={dashboardStats?.users?.totalUsers || '0'}
          icon={<Users className="h-4 w-4" />}
          tone="info"
        />
        <DashboardCard
          title="Outbound Campaigns"
          value={dashboardStats?.campaigns?.totalCampaigns || '0'}
          icon={<Megaphone className="h-4 w-4" />}
          tone={
            Number(dashboardStats?.campaigns?.totalCampaigns ?? 0) > 0
              ? 'positive'
              : 'info'
          }
        />
        <DashboardCard
          title="New Users This Month"
          value={dashboardStats?.users?.totalUsersThisMonth || '0'}
          icon={<UserPlus className="h-4 w-4" />}
          highlight
          tone="positive"
        />
        <DashboardCard
          title="Campaigns This Month"
          value={dashboardStats?.campaigns?.totalCampaignsThisMonth || '0'}
          icon={<CalendarDays className="h-4 w-4" />}
          highlight
          tone="positive"
        />
      </div>
    </>
  );
}
